'use client'

import { motion } from 'framer-motion'
import { Check, X, Minus, Swords } from 'lucide-react'

interface ComparisonColumn {
  name: string
  subtitle?: string
  highlight?: boolean
}

interface ComparisonRow {
  capability: string
  values: (boolean | 'partial')[]
}

interface AutomotiveComparisonSectionProps {
  eyebrow?: string
  heading: string
  intro?: string
  toolsRunLine?: string
  columns: ComparisonColumn[]
  rows: ComparisonRow[]
  wedge?: string
}

function Cell({ value, highlight }: { value: boolean | 'partial'; highlight?: boolean }) {
  if (value === 'partial') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400">
        <Minus className="h-4 w-4" />
      </span>
    )
  }
  if (value) {
    return (
      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${highlight ? 'bg-[#2F5FE8] text-white' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'}`}>
        <Check className="h-4 w-4" />
      </span>
    )
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground/50">
      <X className="h-4 w-4" />
    </span>
  )
}

export function AutomotiveComparisonSection({
  eyebrow,
  heading,
  intro,
  toolsRunLine,
  columns,
  rows,
  wedge,
}: AutomotiveComparisonSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="reveal reveal-delay-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F5FE8]/10 border border-[#2F5FE8]/20 mb-6"
            >
              <Swords className="h-4 w-4 text-[#2F5FE8]" />
              <span className="eyebrow">{eyebrow}</span>
            </motion.div>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
          >
            {heading}
          </motion.h2>
          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="reveal reveal-delay-2 text-lg text-muted-foreground"
            >
              {intro}
            </motion.p>
          )}
        </div>

        {/* Mobile: stacked cards (one per capability) */}
        <div className="md:hidden space-y-4 max-w-md mx-auto">
          {rows.map((row, i) => (
            <motion.div
              key={row.capability}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(i * 0.04, 0.3), duration: 0.4 }}
              className="rounded-2xl border border-border/50 bg-white dark:bg-zinc-900 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.06)]"
            >
              <p className="text-sm font-semibold text-foreground mb-3">{row.capability}</p>
              <div className="space-y-2">
                {columns.map((col, j) => (
                  <div
                    key={col.name}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 ${col.highlight ? 'bg-[#2F5FE8]/5' : 'bg-muted/30'}`}
                  >
                    <span className={`text-xs font-medium ${col.highlight ? 'text-[#2F5FE8]' : 'text-muted-foreground'}`}>
                      {col.name}
                    </span>
                    <Cell value={row.values[j]} highlight={col.highlight} />
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Desktop: full comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="hidden md:block max-w-5xl mx-auto overflow-x-auto rounded-3xl border border-border/50 bg-white dark:bg-zinc-900 shadow-[0_4px_30px_rgba(0,0,0,0.08)]"
        >
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className="border-b border-border/50">
                <th className="p-5 md:p-6 text-sm font-semibold text-muted-foreground align-bottom w-[40%]">
                  What a lead actually needs
                </th>
                {columns.map((col) => (
                  <th
                    key={col.name}
                    className={`p-4 md:p-5 text-center align-bottom ${col.highlight ? 'bg-[#2F5FE8]/5' : ''}`}
                  >
                    <div className={`text-sm md:text-base font-bold ${col.highlight ? 'text-[#2F5FE8]' : 'text-foreground'}`}>
                      {col.name}
                    </div>
                    {col.subtitle && (
                      <div className="mt-1 text-[11px] font-medium text-muted-foreground leading-tight">
                        {col.subtitle}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.capability} className={i % 2 === 1 ? 'bg-muted/30' : ''}>
                  <td className="p-4 md:p-5 text-sm font-medium text-foreground">{row.capability}</td>
                  {row.values.map((value, j) => (
                    <td
                      key={j}
                      className={`p-4 md:p-5 text-center ${columns[j]?.highlight ? 'bg-[#2F5FE8]/5' : ''}`}
                    >
                      <span className="inline-flex justify-center">
                        <Cell value={value} highlight={columns[j]?.highlight} />
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {toolsRunLine && (
          <p className="max-w-5xl mx-auto mt-5 text-center text-xs text-muted-foreground">
            {toolsRunLine}
          </p>
        )}

        {/* Wedge statement */}
        {wedge && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal reveal-delay-1 max-w-3xl mx-auto mt-12 rounded-2xl border-l-4 border-[#2F5FE8] bg-[#2F5FE8]/5 p-6 md:p-8"
          >
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              {wedge}
            </p>
          </motion.div>
        )}
      </div>
    </section>
  )
}
